import moment from "moment";
import { BiSolidTrash } from "react-icons/bi";
import { FaEye } from "react-icons/fa6";
import { MdRefresh } from "react-icons/md";
import { FiPackage, FiMapPin, FiClock, FiArrowRight, FiCopy } from "react-icons/fi";
import { Spinner } from "reactstrap";
import toast from "react-hot-toast";
import { Tooltip } from "antd";
import { getItemQuantity } from "../../utility";

// ─── Status config (shared with FedEx) ─────────────────────────────────────
const STATUS_CONFIG = {
  delivered: { bg: "#ecfdf5", color: "#065f46", border: "#a7f3d0", dot: "#10b981", pulse: false },
  "in-transit": { bg: "#eff6ff", color: "#1e40af", border: "#bfdbfe", dot: "#3b82f6", pulse: true },
  "picked-up": { bg: "#eef2ff", color: "#3730a3", border: "#c7d2fe", dot: "#6366f1", pulse: false },
  "label-created": { bg: "#fffbeb", color: "#92400e", border: "#fde68a", dot: "#f59e0b", pulse: false },
  "out-for-delivery": { bg: "#f0fdf4", color: "#166534", border: "#bbf7d0", dot: "#22c55e", pulse: true },
  exception: { bg: "#fef2f2", color: "#991b1b", border: "#fecaca", dot: "#ef4444", pulse: false },
  pending: { bg: "#f8fafc", color: "#475569", border: "#e2e8f0", dot: "#94a3b8", pulse: false },
};

const resolveUpsStage = (typeCode, statusCode, desc) => {
  const t = (typeCode || "").toUpperCase();
  const c = (statusCode || "").toUpperCase();
  const d = (desc || "").toLowerCase();

  if (t === "D" || c === "KB" || d.includes("delivered")) return "delivered";
  if (c === "OT" || d.includes("out for delivery")) return "out-for-delivery";
  if (c === "MP" || (t === "M" && d.includes("label"))) return "label-created";
  if (["OR", "AR", "DP", "YP", "OF"].includes(c) || t === "I") return "in-transit";
  if (c === "PU" || d.includes("picked up")) return "picked-up";
  return "pending";
};

const titleCase = (str = "") =>
  str.toLowerCase().split(" ").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

// ─── Progress % by delivery stage ──────────────────────────────────────────
const STAGE_PROGRESS = {
  "label-created": { pct: 8, color: "#f59e0b" },
  "pending": { pct: 0, color: "#94a3b8" },
  "picked-up": { pct: 25, color: "#6366f1" },
  "in-transit": { pct: 55, color: "#3b82f6" },
  "out-for-delivery": { pct: 85, color: "#22c55e" },
  "exception": { pct: 50, color: "#ef4444" },
  "delivered": { pct: 100, color: "#10b981" },
};

const RouteProgress = ({ stage, originLabel, destLabel }) => {
  const prog = STAGE_PROGRESS[stage] || STAGE_PROGRESS["pending"];
  const isComplete = stage === "delivered";
  return (
    <div style={{ marginTop: "6px", width: "100%" }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "3px" }}>
        <span style={{ fontSize: "10px", color: "#94a3b8", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "45%" }}>
          {originLabel || "Origin"}
        </span>
        <span style={{ fontSize: "10px", color: isComplete ? "#059669" : "#64748b", fontWeight: isComplete ? 700 : 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "45%", textAlign: "right" }}>
          {destLabel || "Destination"}
        </span>
      </div>
      <div style={{ position: "relative", height: "6px", borderRadius: "10px", background: "#f1f5f9", overflow: "hidden" }}>
        <div style={{
          height: "100%",
          width: `${prog.pct}%`,
          borderRadius: "10px",
          background: isComplete
            ? `linear-gradient(90deg, #34d399, #10b981)`
            : `linear-gradient(90deg, ${prog.color}cc, ${prog.color})`,
          transition: "width 0.4s ease",
        }} />
        {!isComplete && prog.pct > 0 && (
          <div style={{
            position: "absolute",
            top: "-2px",
            left: `calc(${prog.pct}% - 5px)`,
            width: "10px",
            height: "10px",
            borderRadius: "50%",
            background: prog.color,
            border: "2px solid white",
            boxShadow: `0 0 0 2px ${prog.color}44`,
          }} />
        )}
      </div>
      <div style={{ textAlign: "right", fontSize: "9px", color: prog.color, fontWeight: 700, marginTop: "2px" }}>
        {isComplete ? "✓ Delivered" : `${prog.pct}%`}
      </div>
    </div>
  );
};

// Parse UPS date "20260512" + time "111101" → moment
const parseUpsDateTime = (date, time) => {
  if (!date || date.length < 8) return null;
  const timeStr = (time || "000000").padStart(6, "0");
  return moment(`${date} ${timeStr}`, "YYYYMMDD HHmmss");
};

// ─── Component ──────────────────────────────────────────────────────────────
export const TrackingUpsItem = ({
  trackingData,
  labelImage,
  deleteTracking,
  trackingType,
  line_items,
  product,
  id,
  date,
  shippingCost,
  refreshTrackingData,
  loading,
}) => {

  // ════════════════════════════════════════════════════════════════════
  // DATA EXTRACTION
  //
  // UPS structure:
  //   trackingData.trackResponse.shipment[0].package[0]
  //
  // Key fields:
  //   package[0].activity[]          → tracking events (newest first)
  //   package[0].currentStatus       → { code, description }
  //   package[0].weight              → { weight, unitOfMeasurement }
  //   package[0].service             → { description }
  //   package[0].packageAddress[]    → [{type:"ORIGIN",...}, {type:"DESTINATION",...}]
  //   package[0].deliveryInformation → { receivedBy, location }
  //   shipment[0].inquiryNumber      → tracking number
  // ════════════════════════════════════════════════════════════════════

  const shipment = trackingData?.trackResponse?.shipment?.[0];
  const pkg = shipment?.package?.[0];
  const activity = Array.isArray(pkg?.activity) ? pkg.activity : [];
  const latestAct = activity.length > 0 ? activity[0] : null;

  // ── Service name ──────────────────────────────────────────────────
  const serviceName = pkg?.service?.description || "UPS";

  // ── Tracking number ───────────────────────────────────────────────
  const trackNum = trackingData?.trackingNumber || shipment?.inquiryNumber || shipment?.trackingNumber || "";

  // ── Weight ────────────────────────────────────────────────────────
  const weightVal = pkg?.weight?.weight;
  const weightUnit = pkg?.weight?.unitOfMeasurement || "LBS";

  // ── Status ────────────────────────────────────────────────────────
  const latestStatus = latestAct?.status || pkg?.currentStatus;
  const statusType = latestAct?.status?.type || "";
  const statusCode = latestAct?.status?.code || pkg?.currentStatus?.code || "";
  const statusText = (latestAct?.status?.description || pkg?.currentStatus?.description || "In Transit").trim();

  const stage = resolveUpsStage(statusType, statusCode, statusText);
  const statusStyle = STATUS_CONFIG[stage];

  // ── Date ──────────────────────────────────────────────────────────
  let dateDisplay = null;
  let dateSubLabel = null;
  let isAwaitingPickup = false;

  if (latestAct?.date) {
    const m = parseUpsDateTime(latestAct.date, latestAct.time);
    if (m && m.isValid()) {
      dateDisplay = m.format("MMM DD, YYYY");
      dateSubLabel = m.format("h:mm A");
    }
  }

  if (!dateDisplay) {
    // "MP" type = label created, UPS hasn't received it yet
    if (stage === "label-created") {
      isAwaitingPickup = true;
    } else if (date) {
      dateDisplay = moment(date).format("MMM DD, YYYY");
    }
  }

  // ── Delivery info (for delivered) ────────────────────────────────
  const deliveryInfo = pkg?.deliveryInformation;
  const receivedBy = deliveryInfo?.receivedBy || deliveryInfo?.deliveryPhoto?.receivedBy;
  const deliveredAt = deliveryInfo?.location || deliveryInfo?.deliveryPhoto?.location;

  // Actual delivery date from deliveryDate array
  // deliveryTime is nested inside deliveryInformation
  const deliveryDateEntry = (pkg?.deliveryDate || []).find((d) => d.type === "DEL");
  const deliveryDateStr = deliveryDateEntry?.date;
  const deliveryTimeParsed = deliveryInfo?.deliveryTime?.endTime;  // ← correct path
  const deliveryMoment = deliveryDateStr
    ? parseUpsDateTime(deliveryDateStr, deliveryTimeParsed)
    : null;
  const deliveryDisplay = deliveryMoment?.isValid()
    ? deliveryMoment.format("MMM DD, YYYY [at] h:mm A")
    : null;

  // ── Addresses ────────────────────────────────────────────────────
  const addresses = Array.isArray(pkg?.packageAddress) ? pkg.packageAddress : [];
  const originAddr = addresses.find((a) => a.type === "ORIGIN")?.address;
  const destAddr = addresses.find((a) => a.type === "DESTINATION")?.address;

  // Also check latest activity location as "current" for in-transit
  const currentLocCity = latestAct?.location?.address?.city;
  const currentLocState = latestAct?.location?.address?.stateProvince;

  const originLabel = originAddr?.city && originAddr?.stateProvince
    ? `${titleCase(originAddr.city)}, ${originAddr.stateProvince}`
    : null;
  const destLabel = destAddr?.city && destAddr?.stateProvince
    ? `${titleCase(destAddr.city)}, ${destAddr.stateProvince}`
    : null;

  // ── Dimensions (optional) ─────────────────────────────────────────
  const dim = pkg?.dimension;
  const dimStr = dim?.length && dim?.width && dim?.height
    ? `${dim.length}×${dim.width}×${dim.height} ${dim.unitOfDimension || "IN"}`
    : null;

  // ── UI helpers ────────────────────────────────────────────────────
  const isDirectShip = trackingType === "Direct Ship" || !trackingType;
  const isComingToUS = trackingType === "Coming To Techmicro";

  const accentColor = isDirectShip ? "#22c55e" : isComingToUS ? "#3b82f6" : "#64748b";
  const containerBg = isDirectShip ? "#f0fdf4" : isComingToUS ? "#eff6ff" : "#f8fafc";
  const containerBorder = isDirectShip ? "1px solid #86efac" : isComingToUS ? "1px solid #bfdbfe" : "1px solid #e2e8f0";
  const containerShadow = isDirectShip ? "0 4px 6px rgba(34,197,94,0.15)" : isComingToUS ? "0 4px 6px rgba(59,130,246,0.15)" : "0 4px 6px rgba(100,116,139,0.15)";
  const typeBg = isDirectShip ? "#f0fdf4" : isComingToUS ? "#eff6ff" : "#f8fafc";
  const typeColor = isDirectShip ? "#166534" : isComingToUS ? "#1d4ed8" : "#475569";
  const typeBorder = isDirectShip ? "#bbf7d0" : isComingToUS ? "#bfdbfe" : "#e2e8f0";

  const qty = getItemQuantity(line_items, product);

  const cell = (flexBasis, extra = {}) => ({
    flex: `0 1 ${flexBasis}`,
    minWidth: 0,
    padding: "0 8px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    overflow: "hidden",
    ...extra,
  });

  const label = (txt) => (
    <span style={{ fontSize: "10px", fontWeight: 700, color: "#1e293b", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "2px" }}>
      {txt}
    </span>
  );

  return (
    <div style={{
      display: "flex",
      alignItems: "stretch",
      width: "100%",
      marginBottom: "6px",
      borderRadius: "8px",
      overflow: "hidden",
      border: containerBorder,
      background: containerBg,
      boxShadow: containerShadow,
      minHeight: "60px",
      boxSizing: "border-box",
    }}>

      {/* Accent bar */}
      <div style={{ width: "4px", background: accentColor, flexShrink: 0 }} />

      {/* ── CARRIER + TRACKING # ── */}
      <div style={cell("17%", { padding: "8px 10px", borderRight: "1px dashed #94a3b8" })}>
        <div style={{ display: "flex", alignItems: "center", gap: "5px", marginBottom: "3px" }}>
          <span style={{
            background: "#f59e0b", color: "#1c1917",
            fontSize: "9px", fontWeight: 800,
            padding: "1px 6px", borderRadius: "3px", letterSpacing: "0.5px",
          }}>UPS</span>
          <span style={{ fontSize: "11px", color: "#94a3b8", fontWeight: 500 }}>
            {serviceName}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <a
            href={`https://www.ups.com/track?tracknum=${trackNum}`}
            target="_blank" rel="noreferrer"
            style={{ fontSize: "13px", fontWeight: 700, color: "#0369a1", textDecoration: "none", fontFamily: "monospace", letterSpacing: "0.3px", lineHeight: 1.2 }}
          >
            {trackNum}
          </a>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              navigator.clipboard.writeText(trackNum);
              toast.success("Copied to clipboard");
            }}
            style={{ background: "none", border: "none", padding: "2px", cursor: "pointer", display: "flex", alignItems: "center" }}
            title="Copy Tracking Number"
          >
            <FiCopy size={13} color="#64748b" />
          </button>
        </div>
      </div>

      {/* ── STATUS ── */}
      <div style={cell("21%", { padding: "8px 10px", borderRight: "1px dashed #94a3b8" })}>
        <span style={{
          display: "inline-flex", alignItems: "center", gap: "5px",
          padding: "3px 10px", borderRadius: "20px", fontSize: "12px", fontWeight: 700,
          backgroundColor: statusStyle.bg, color: statusStyle.color,
          border: `1px solid ${statusStyle.border}`,
          width: "fit-content",
        }}>
          <span style={{
            width: "7px", height: "7px", borderRadius: "50%",
            backgroundColor: statusStyle.dot, display: "inline-block", flexShrink: 0,
            animation: statusStyle.pulse ? "statusDotPulse 1.5s infinite" : "none",
          }} />
          {statusText}
        </span>

        {/* Sub text */}
        {stage === "delivered" && deliveryDisplay ? (
          <span style={{ fontSize: "11px", color: "#059669", marginTop: "3px", fontWeight: 600 }}>
            {deliveryDisplay}{receivedBy ? ` · Rcvd by ${receivedBy}` : ""}
            {deliveredAt ? ` · ${deliveredAt}` : ""}
          </span>
        ) : isAwaitingPickup ? (
          <span style={{ fontSize: "11px", color: "#94a3b8", marginTop: "3px", display: "flex", alignItems: "center", gap: "3px" }}>
            <FiClock size={9} />
            UPS hasn't received the package yet
          </span>
        ) : latestAct?.status?.description && latestAct.status.description.trim().toLowerCase() !== statusText.toLowerCase() ? (
          <span style={{ fontSize: "11px", color: "#64748b", marginTop: "3px" }}>
            {latestAct.status.description.trim()}
          </span>
        ) : null}
      </div>

      {/* ── ROUTE + PROGRESS ── */}
      <div style={cell("19%", { padding: "8px 10px", borderRight: "1px dashed #94a3b8" })}>
        {label("Route")}
        {(originLabel || destLabel) ? (
          <div style={{ width: "100%" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "4px", flexWrap: "nowrap", overflow: "hidden", fontSize: "12px" }}>
              {originLabel && (
                <span style={{ display: "flex", alignItems: "center", gap: "2px", color: "#64748b", fontWeight: 500, fontSize: "11px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "42%" }}>
                  <FiMapPin size={9} color="#94a3b8" />
                  {originLabel}
                </span>
              )}
              {originLabel && destLabel && <FiArrowRight size={9} color="#cbd5e1" style={{ flexShrink: 0 }} />}
              {destLabel && (
                <span style={{ display: "flex", alignItems: "center", gap: "2px", fontWeight: 700, color: "#334155", fontSize: "11px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "42%" }}>
                  <FiMapPin size={9} color={stage === "delivered" ? "#10b981" : "#f59e0b"} />
                  {destLabel}
                </span>
              )}
            </div>
            {originLabel && destLabel && (
              <RouteProgress stage={stage} originLabel={originLabel} destLabel={destLabel} />
            )}
          </div>
        ) : currentLocCity && currentLocState ? (
          <span style={{ fontSize: "12px", color: "#64748b", display: "flex", alignItems: "center", gap: "3px" }}>
            <FiMapPin size={10} color="#94a3b8" />
            {titleCase(currentLocCity)}, {currentLocState}
          </span>
        ) : (
          <span style={{ fontSize: "11px", color: "#cbd5e1" }}>—</span>
        )}
      </div>

      {/* ── DATE ── */}
      <div style={cell("13%", { padding: "8px 8px", borderRight: "1px dashed #94a3b8" })}>
        {label("Last Update")}
        {isAwaitingPickup ? (
          <span style={{ fontSize: "12px", color: "#94a3b8", display: "flex", alignItems: "center", gap: "3px" }}>
            <FiClock size={10} />
            Not picked up
          </span>
        ) : (
          <>
            <span style={{ fontSize: "13px", fontWeight: 600, color: "#334155" }}>{dateDisplay}</span>
            {dateSubLabel && <span style={{ fontSize: "11px", color: "#94a3b8" }}>{dateSubLabel}</span>}
          </>
        )}
      </div>

      {/* ── PACKAGE ── */}
      <div style={cell("13%", { padding: "8px 8px", borderRight: "1px dashed #94a3b8" })}>
        {label("Package")}
        <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
          {weightVal && (
            <span style={{ fontSize: "13px", fontWeight: 600, color: "#334155" }}>
              ⚖ {weightVal} <span style={{ fontSize: "11px", fontWeight: 400, color: "#94a3b8" }}>{weightUnit}</span>
            </span>
          )}
          <span style={{ fontSize: "13px", color: "#64748b", display: "flex", alignItems: "center", gap: "3px" }}>
            <FiPackage size={11} color="#94a3b8" />
            Qty: <strong style={{ color: "#334155" }}>{qty}</strong>
          </span>
          {dimStr && (
            <span style={{ fontSize: "10px", color: "#94a3b8" }}>{dimStr}</span>
          )}
          {shippingCost && shippingCost !== "N/A" && (
            <span style={{ fontSize: "12px", color: "#94a3b8" }}>Cost: <strong style={{ color: "#475569" }}>{shippingCost}</strong></span>
          )}
        </div>
      </div>

      {/* ── TYPE ── */}
      <div style={cell("10%", { padding: "8px 8px", borderRight: "1px dashed #94a3b8" })}>
        {label("Type")}
        <div style={{ display: "flex", flexDirection: "column", gap: "3px" }}>
          {trackingType && (
            <span style={{
              padding: "2px 8px", borderRadius: "10px", fontSize: "11px", fontWeight: 700,
              backgroundColor: typeBg,
              color: typeColor,
              border: `1px solid ${typeBorder}`,
              width: "fit-content",
            }}>{trackingType}</span>
          )}
        </div>
      </div>

      {/* ── ACTIONS — fixed 104px ── */}
      <div style={{
        flexShrink: 0, flexGrow: 0, width: "104px",
        display: "flex", alignItems: "center", justifyContent: "center",
        gap: "12px", padding: "8px 10px", backgroundColor: "#fafafa",
      }}>
        {labelImage && (
          <button title="View Label" onClick={() => window.open(labelImage, "_blank")}
            style={{ background: "none", border: "none", cursor: "pointer", padding: "3px", display: "flex", alignItems: "center" }}>
            <FaEye color="#0369a1" size={19} />
          </button>
        )}
        {loading ? (
          <Spinner size="sm" color="primary" />
        ) : (
          <Tooltip title="Reload to see the latest status of tracking">
            <button onClick={() => refreshTrackingData(id)}
              style={{ background: "none", border: "none", cursor: "pointer", padding: "3px", display: "flex", alignItems: "center" }}>
              <MdRefresh color="#0ea5e9" size={21} />
            </button>
          </Tooltip>
        )}
        <button title="Delete" onClick={() => deleteTracking(id)}
          style={{ background: "none", border: "none", cursor: "pointer", padding: "3px", display: "flex", alignItems: "center" }}>
          <BiSolidTrash color="#ef4444" size={19} />
        </button>
      </div>
    </div>
  );
};
